import React, { useState, useEffect, useRef } from 'react';

const styles = {
  container: {
    fontSize: 14,
    position: 'relative',
    userSelect: 'none',
    display: 'flex',
    flexDirection: 'column',
    zIndex: 40,
  },
  dropdown: {
    fontSize: 14,
    cursor: 'pointer',
    backgroundColor: '#F2F2F2',
    color: '#AAA',
    minWidth: '220px',
    width: 'auto',
    display: 'flex',
    borderRadius: '4px',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  arrowContainer: {
    backgroundColor: '#01b3b8',
    width: 24,
    height: 24,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginLeft: 8,
    borderRadius: '0px 4px 4px 0px',
  },
  arrowSvg: {
    width: 18,
    height: 18,
    fill: 'white',
    transition: 'transform 0.2s ease',
  },
  arrowOpen: {
    transform: 'rotate(180deg)',
  },
  dropdownList: {
    position: 'relative',
    top: 'calc(100% + 4px)',
    left: 0,
    right: 0,
    maxHeight: 300,
    overflowY: 'auto',
    backgroundColor: '#F2F2F2',
    zIndex: 1000,
    boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
    padding: '4px 0',
  },
  dropdownItem: {
    padding: '6px 10px',
    color: '#333',
    cursor: 'pointer',
    borderBottom: '1px solid rgba(255,255,255,0.15)',
    display: 'flex',
    alignItems: 'center',
  },
  dropdownItemHover: {
    backgroundColor: '#028a90',
    color: 'white',
  },
  checkbox: {
    marginRight: 8,
    cursor: 'pointer',
  },
  selectedText: {
    whiteSpace: 'nowrap',
    flexGrow: 1,
  },
  placeholderText: {
    opacity: 0.6,
  },
};

const ZoneFilter = ({ zones = [], selectedZones = [], onChange }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [hoveredItem, setHoveredItem] = useState(null);
  const dropdownRef = useRef(null);

  // "All" option always first
  const options = ['All', ...zones];

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const toggleOption = (option) => {
    if (option === 'All') {
      // Clear all selections
      onChange([]);
      setIsOpen(false);
      return;
    }

    if (selectedZones.includes(option)) {
      // Remove option
      onChange(selectedZones.filter((z) => z !== option));
    } else {
      // Add option
      onChange([...selectedZones, option]);
    }
  };

  // Display summary text
  const displayText =
    selectedZones.length === 0
      ? 'Filter zones (All)'
      : selectedZones.length > 2
      ? `${selectedZones[0]} +${selectedZones.length - 1} more`
      : selectedZones.join(', ');

  return (
    <div style={styles.container} title="Filter by document 1 zones (multi-select)" ref={dropdownRef}>
      <div
        style={styles.dropdown}
        onClick={() => setIsOpen(!isOpen)}
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            setIsOpen(!isOpen);
          } else if (e.key === 'Escape') {
            setIsOpen(false);
          }
        }}
        role="combobox"
        aria-haspopup="listbox"
        aria-expanded={isOpen}
      >
        <span
          style={{
            ...styles.selectedText,
            ...(selectedZones.length === 0 ? styles.placeholderText : {}),
          }}
        >
          {displayText}
        </span>
        <div style={styles.arrowContainer}>
          <svg
            style={{
              ...styles.arrowSvg,
              ...(isOpen ? styles.arrowOpen : {}),
            }}
            viewBox="0 0 24 24"
            xmlns="http://www.w3.org/2000/svg"
            aria-hidden="true"
            focusable="false"
          >
            <path d="M7 10l5 5 5-5z" />
          </svg>
        </div>
      </div>

      {isOpen && (
        <div style={styles.dropdownList} role="listbox" tabIndex={-1}>
          {options.map((option) => {
            const isSelected = option === 'All' ? selectedZones.length === 0 : selectedZones.includes(option);
            return (
              <div
                key={option}
                role="option"
                aria-selected={isSelected}
                tabIndex={0}
                onClick={() => toggleOption(option)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    toggleOption(option);
                  }
                }}
                onMouseEnter={() => setHoveredItem(option)}
                onMouseLeave={() => setHoveredItem(null)}
                style={{
                  ...styles.dropdownItem,
                  ...(hoveredItem === option ? styles.dropdownItemHover : {}),
                  fontWeight: isSelected ? 'bold' : 'normal',
                }}
              >
                <input
                  type="checkbox"
                  checked={isSelected}
                  readOnly
                  tabIndex={-1}
                  style={styles.checkbox}
                  aria-label={`Select zone ${option}`}
                />
                {option}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default ZoneFilter;
